import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Plus, Trash2, Loader2, Send, Download } from "lucide-react";
import { format } from "date-fns";

interface InvoiceItem {
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
}

interface InvoiceBuilderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId?: string;
  serviceRequestId?: string;
  onSuccess: () => void;
}

export function InvoiceBuilder({ open, onOpenChange, projectId, serviceRequestId, onSuccess }: InvoiceBuilderProps) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [selectedProjectId, setSelectedProjectId] = useState<string>(projectId || "");

  const { data: clients } = useQuery({
    queryKey: ["clients-for-invoice"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Fetch both manual customers and auth users (profiles with client type)
      const [customersResult, profilesResult] = await Promise.all([
        supabase.from("customers").select("*").order("name"),
        supabase
          .from("profiles")
          .select("*")
          .eq("user_type", "client")
          .order("email")
      ]);

      if (customersResult.error) throw customersResult.error;
      if (profilesResult.error) throw profilesResult.error;

      // Combine both sources
      const allClients = [
        ...(customersResult.data || []).map((c: any) => ({
          id: c.id,
          name: c.name,
          email: c.email,
          address: c.address,
          type: "manual" as const,
        })),
        ...(profilesResult.data || []).map((p: any) => ({
          id: p.id,
          name: `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email,
          email: p.email,
          address: null,
          type: "auth" as const,
        }))
      ];

      return allClients;
    },
  });

  const { data: projects } = useQuery({
    queryKey: ["projects-for-invoice", selectedClientId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      let query = supabase
        .from("projects")
        .select("*")
        .order("created_at", { ascending: false });

      if (selectedClientId) {
        query = query.eq("customer_id", selectedClientId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: open,
  });

  const { data: serviceRequests } = useQuery({
    queryKey: ["service-requests-for-invoice"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_requests")
        .select("id, service_type, business_name, contact_name, status")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  const { data: companySettings } = useQuery({
    queryKey: ["company-settings"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("company_settings")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
  });

  const [formData, setFormData] = useState({
    client_name: "",
    client_email: "",
    client_address: "",
    issue_date: format(new Date(), "yyyy-MM-dd"),
    due_date: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"),
    tax_rate: 0,
    discount_amount: 0,
    notes: companySettings?.invoice_notes || "",
    terms: companySettings?.invoice_terms || "",
    payment_instructions: "",
  });

  const [items, setItems] = useState<InvoiceItem[]>([
    { description: "", quantity: 1, unit_price: 0, amount: 0 },
  ]);

  // Auto-fill client info when client is selected
  useEffect(() => {
    if (selectedClientId && clients) {
      const client = clients.find(c => c.id === selectedClientId);
      if (client) {
        setFormData(prev => ({
          ...prev,
          client_name: client.name,
          client_email: client.email || "",
          client_address: client.address || "",
        }));
      }
    }
  }, [selectedClientId, clients]);

  // Auto-select project if provided
  useEffect(() => {
    if (projectId) {
      setSelectedProjectId(projectId);
    }
  }, [projectId]);

  const addItem = () => {
    setItems([...items, { description: "", quantity: 1, unit_price: 0, amount: 0 }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index: number, field: keyof InvoiceItem, value: string | number) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    if (field === "quantity" || field === "unit_price") {
      newItems[index].amount = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setItems(newItems);
  };

  const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
  const taxAmount = (subtotal * formData.tax_rate) / 100;
  const total = subtotal + taxAmount - formData.discount_amount;

  const handleSaveAndSend = async (shouldSend: boolean) => {
    if (!companySettings) {
      toast({
        title: "Error",
        description: "Please configure company settings first",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const invoiceNumber = `${companySettings.invoice_prefix}-${String(companySettings.invoice_next_number).padStart(4, "0")}`;

      // Create invoice
      // Parse project/service request ID
      let finalProjectId = projectId || null;
      let finalServiceRequestId = serviceRequestId || null;
      
      if (selectedProjectId) {
        if (selectedProjectId.startsWith('sr-')) {
          finalServiceRequestId = selectedProjectId.replace('sr-', '');
        } else {
          finalProjectId = selectedProjectId;
        }
      }

      const { data: invoice, error: invoiceError } = await supabase
        .from("invoices")
        .insert({
          user_id: user.id,
          invoice_number: invoiceNumber,
          client_name: formData.client_name,
          client_email: formData.client_email,
          client_address: formData.client_address,
          client_id: selectedClientId || null,
          project_id: finalProjectId,
          service_request_id: finalServiceRequestId,
          status: shouldSend ? "sent" : "draft",
          issue_date: formData.issue_date,
          due_date: formData.due_date,
          subtotal: subtotal,
          tax_rate: formData.tax_rate,
          tax_amount: taxAmount,
          discount_amount: formData.discount_amount,
          total_amount: total,
          currency: companySettings.currency,
          notes: formData.notes,
          terms: formData.terms,
          payment_instructions: formData.payment_instructions,
        })
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // Create invoice items
      const itemsToInsert = items.map((item) => ({
        invoice_id: invoice.id,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        amount: item.amount,
      }));

      const { error: itemsError } = await supabase
        .from("invoice_items")
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;

      // Update next invoice number
      await supabase
        .from("company_settings")
        .update({ invoice_next_number: companySettings.invoice_next_number + 1 })
        .eq("user_id", user.id);

      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["company-settings"] });

      if (shouldSend) {
        toast({
          title: "Success",
          description: "Invoice created and email notification sent to client",
        });
      } else {
        // Generate PDF download for draft
        await generatePDF(invoice, itemsToInsert, companySettings);
        toast({
          title: "Success",
          description: "Invoice draft created and downloaded",
        });
      }

      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async (invoice: any, items: any[], company: any) => {
    // Create a simple HTML invoice
    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Invoice ${invoice.invoice_number}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; }
          .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
          .company { font-size: 24px; font-weight: bold; }
          .invoice-details { text-align: right; }
          .client-info { margin-bottom: 30px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background-color: #f5f5f5; }
          .totals { margin-top: 20px; text-align: right; }
          .total-row { display: flex; justify-content: flex-end; margin: 5px 0; }
          .total-label { width: 150px; text-align: right; margin-right: 20px; }
          .total-amount { width: 150px; text-align: right; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="company">${company.company_name}</div>
            <div>${company.company_address || ''}</div>
            <div>${company.company_email || ''}</div>
            <div>${company.company_phone || ''}</div>
          </div>
          <div class="invoice-details">
            <h1>INVOICE</h1>
            <div><strong>Invoice #:</strong> ${invoice.invoice_number}</div>
            <div><strong>Date:</strong> ${format(new Date(invoice.issue_date), "MMM dd, yyyy")}</div>
            <div><strong>Due:</strong> ${format(new Date(invoice.due_date), "MMM dd, yyyy")}</div>
          </div>
        </div>
        
        <div class="client-info">
          <strong>Bill To:</strong><br>
          ${invoice.client_name}<br>
          ${invoice.client_email}<br>
          ${invoice.client_address || ''}
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Quantity</th>
              <th>Unit Price</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            ${items.map(item => `
              <tr>
                <td>${item.description}</td>
                <td>${item.quantity}</td>
                <td>${invoice.currency} ${item.unit_price.toFixed(2)}</td>
                <td>${invoice.currency} ${item.amount.toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="totals">
          <div class="total-row">
            <div class="total-label">Subtotal:</div>
            <div class="total-amount">${invoice.currency} ${invoice.subtotal.toFixed(2)}</div>
          </div>
          <div class="total-row">
            <div class="total-label">Tax (${invoice.tax_rate}%):</div>
            <div class="total-amount">${invoice.currency} ${invoice.tax_amount.toFixed(2)}</div>
          </div>
          ${invoice.discount_amount > 0 ? `
          <div class="total-row">
            <div class="total-label">Discount:</div>
            <div class="total-amount">-${invoice.currency} ${invoice.discount_amount.toFixed(2)}</div>
          </div>
          ` : ''}
          <div class="total-row" style="font-size: 18px; margin-top: 10px; padding-top: 10px; border-top: 2px solid #333;">
            <div class="total-label">Total:</div>
            <div class="total-amount">${invoice.currency} ${invoice.total_amount.toFixed(2)}</div>
          </div>
        </div>
        
        ${invoice.notes ? `<div style="margin-top: 40px;"><strong>Notes:</strong><br>${invoice.notes}</div>` : ''}
        ${invoice.terms ? `<div style="margin-top: 20px;"><strong>Terms & Conditions:</strong><br>${invoice.terms}</div>` : ''}
        ${invoice.payment_instructions ? `<div style="margin-top: 20px;"><strong>Payment Instructions:</strong><br>${invoice.payment_instructions}</div>` : ''}
      </body>
      </html>
    `;

    // Create a blob and download
    const blob = new Blob([invoiceHTML], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${invoice.invoice_number}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast({
      title: "Success",
      description: "Invoice saved as draft and downloaded",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Invoice</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Client and Project Selection */}
          <div className="space-y-4">
            <h3 className="font-semibold">Select Client & Project (Optional)</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Select Client</Label>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose existing client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients?.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} - {client.email}
                        {client.type === "auth" && (
                          <span className="ml-2 text-xs text-muted-foreground">(Platform User)</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Select Project/Service</Label>
                <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose project or service..." />
                  </SelectTrigger>
                  <SelectContent>
                    {projects && projects.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
                          Projects
                        </div>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {serviceRequests && serviceRequests.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
                          Service Requests
                        </div>
                        {serviceRequests.map((request) => (
                          <SelectItem key={`sr-${request.id}`} value={`sr-${request.id}`}>
                            {request.service_type} - {request.business_name || request.contact_name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Client Information */}
          <div className="space-y-4">
            <h3 className="font-semibold">Client Information</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Client Name *</Label>
                <Input
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Client Email *</Label>
                <Input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Client Address</Label>
              <Textarea
                value={formData.client_address}
                onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          {/* Invoice Details */}
          <div className="space-y-4">
            <h3 className="font-semibold">Invoice Details</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Issue Date</Label>
                <Input
                  type="date"
                  value={formData.issue_date}
                  onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Line Items</h3>
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                <Plus className="h-4 w-4 mr-1" />
                Add Item
              </Button>
            </div>
            
            {items.map((item, index) => (
              <div key={index} className="grid gap-2 md:grid-cols-12 items-end p-4 border rounded-lg">
                <div className="md:col-span-5 space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={item.description}
                    onChange={(e) => updateItem(index, "description", e.target.value)}
                    placeholder="Service or product"
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateItem(index, "quantity", parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label>Unit Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={item.unit_price}
                    onChange={(e) => updateItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label>Amount</Label>
                  <Input value={item.amount.toFixed(2)} disabled />
                </div>
                <div className="md:col-span-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeItem(index)}
                    disabled={items.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="border-t pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal:</span>
              <span className="font-medium">{companySettings?.currency} {subtotal.toFixed(2)}</span>
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Discount Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.discount_amount}
                  onChange={(e) => setFormData({ ...formData, discount_amount: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="flex justify-between text-sm">
              <span>Tax:</span>
              <span className="font-medium">{companySettings?.currency} {taxAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t pt-2">
              <span>Total:</span>
              <span>{companySettings?.currency} {total.toFixed(2)}</span>
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Terms & Conditions</Label>
              <Textarea
                value={formData.terms}
                onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Instructions</Label>
              <Textarea
                value={formData.payment_instructions}
                onChange={(e) => setFormData({ ...formData, payment_instructions: e.target.value })}
                rows={2}
                placeholder="Bank transfer details, payment methods, etc."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={() => handleSaveAndSend(false)} disabled={loading} className="flex-1" variant="outline">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Save & Download
                </>
              )}
            </Button>
            <Button onClick={() => handleSaveAndSend(true)} disabled={loading} className="flex-1">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Invoice
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
